import StekzHTMLElement from "@/wc/registry/component/base/index.js";
import customElement from "@/wc/registry/lib/decorator/customElement/index.js";
import type IFormInputTypeComponent from "@/wc/registry/types/form-input-type";
import { styles as bulmaStyles } from "@/wc/registry/lib/styles/bulma/index.js";
import { styles as fontAwesomeStyles } from "@/wc/registry/lib/styles/fontawesome/index.js";
//import externalStyle from "@/wc/registry/component/form-input/module.styles.css";
import * as form from "@/wc/registry/lib/form/index.js";

// Import all form-inputs to avoid dynamically constructed imports
import "@/wc/registry/component/form-input-choice/index.js";
import "@/wc/registry/component/form-input-string/index.js";
import "@/wc/registry/component/form-input-textarea/index.js";
/**
  Provides a mapping for types that can be passed as a prop to c-form-input
  and the html that needs to be rendered because of it. Currently it
  implements three types.

  Each type should implement getTemplate and props, so that we could
  give of a warning when the user uses the `c-form-input` element
  incorrectly.

  E.g. using <c-form-input type="string" placeholder="Joe"></c-form-input>
  would throw an error, because the "label" prop is not set yet.

  Format of type: {
    "date-range": { // Key of the type configuration
      component: 'date-2',  // Actual input component may be different (see below)
      requiredProps: [
        "label"
      ],
      optionalProps: [
        "readOnly"
      ],
      secretProps: {
        mode: "range"
      },
    },
  }
  * The actual "component" in the specified type may be different than the key of the type in the supportedTypes.
    This is used primarily when we have a single component that has multiple capabilities
    such as a datepicker that has date-range, single or time capabilities all in one.
    We want to separate out the types in the scenario.

  * Secret properties are those that are internally set within this form-input component.
    E.g. "mode" might be a specific property for the date-range needed for the c-form-input-date-selector
  * Required standard properties are those that will throw an error unless specified.
  * Optional standard properties are those that will not throw an error if not specified.
  * NOTE! A property can only appear in one of the 2 categories (requiredProps or optionalProps) not both.
    E.g. A property of "label" will only appear in requiredProps or optionalProps.
*/
const supportedTypes = {
    "textarea": {
        component: "textarea",
        requiredProps: [],
        optionalProps: [
            "required",
            "readOnly",
            "rows",
            "horizontal",
            "helperText",
            "inline",
            "label",
            "placeholder",
            "appendValue",
        ],
        secretProps: {},
    },
    "string": {
        component: "string",
        requiredProps: [],
        optionalProps: [
            "required",
            "readOnly",
            "helperText",
            "horizontal",
            "inline",
            "label",
            "placeholder",
            "appendValue",
            "prependValue",
        ],
        secretProps: {},
    },
    "choice": {
        component: "choice",
        requiredProps: [],
        // Setting either 'choices' or 'service-repository' is mandatory, but
        // this is not yet enforced.
        optionalProps: [
            "required",
            "placeholder",
            "readOnly",
            "choices",
            "serviceRepository",
            "serviceRepositoryParameters",
            "helperText",
            "dependsOn",
            "key",
            "horizontal",
            "useAsContext",
            "keyDescription",
            "prependValue",
            "appendValue",
            "avoidSort",
            "inline",
            "label",
            "parseType",
        ],
        secretProps: {},
    },
};

// An input component which aims to provide pretty form elements for certain
// input types. This component works together with c-form, and maps types of
// inputs to implementations of the specific form elements.
@customElement("c-form-input", {
    style: fetch("/wc/registry/component/form-input/styles.css").then((res) =>
        res.text()
    ),
    template: undefined,
    extraStyles: [bulmaStyles, fontAwesomeStyles],
})
export default class FormInput<
    ValueType = string | number | boolean
> extends StekzHTMLElement {
    // Return a list for attributes which should be observed. When an observed
    // attribute is changed, attributeChangedCallback is called.
    static get observedAttributes() {
        // Attributes must be lower case.
        return [
            "data-value",
            "data-choices",
            "data-read-only",
            "data-append-value",
            "data-prepend-value",
            "data-meta-entries",
            "data-min",
            "data-max",
            "data-selected-label",
        ];
    }

    set name(value: string) {
        this.dataset.name = value;
    }
    get name() {
        return this.dataset.name;
    }

    set params(value: string) {
        this.dataset.params = value;
    }
    get params() {
        return this.dataset.params;
    }

    get avoidShadowRoot() {
        return typeof this.dataset.avoidShadowRoot !== "undefined";
    }

    get serviceRepository() {
        return this.dataset.serviceRepository;
    }

    set type(value: string) {
        this.dataset.type = value;
    }
    get type() {
        return this.dataset.type || "string";
    }

    get targetComponentTagName() {
        const props = supportedTypes[this.type];

        if (!("component" in props) && !props.component) {
            throw new Error(
                `'${this.type}' requires the component to be set in the form-input supportedTypes'.`
            );
        } else {
            return props.component;
        }
    }

    set value(value: ValueType) {
        this.dataset.value = `${value}`;

        const nestedInputComponent = this.getNestedInputComponent();
        if (nestedInputComponent) {
            nestedInputComponent.value = value;
        }
    }
    get value(): ValueType | string {
        const nestedInputComponent = this.getNestedInputComponent();
        if (nestedInputComponent) {
            return nestedInputComponent.value;
        }

        // There is a chance that the input element is not rendered
        // yet, in that case we return the value from the dataset instead.
        return this.dataset.value;
    }

    get requiresPreprocessing(): boolean {
        return (
            "requiresPreprocessing" in supportedTypes[this.type] &&
            supportedTypes[this.type]["requiresPreprocessing"] === true
        );
    }

    set error(value: string) {
        const nestedInputComponent = this.getNestedInputComponent();
        if (nestedInputComponent) {
            nestedInputComponent.error = value;
        }
    }

    reset() {
        const nestedInputComponent = this.getNestedInputComponent();
        if (nestedInputComponent) {
            nestedInputComponent.reset();
        }
    }

    // Tell the nested component to render all the values to the template by
    // iterating over all 'data-<name>' attributes.
    updateValues() {
        const nestedInputComponent = this.getNestedInputComponent();
        if (nestedInputComponent) {
            nestedInputComponent.updateValues();
        }
    }

    // This allows a form to set parameters required by a child component.
    // Extra parameters are sometimes required by child components to be able
    // retrieve data from a repository.
    setParameter(name: string, value: string) {
        const nestedInputComponent = this.getNestedInputComponent();
        if (nestedInputComponent) {
            if (value) {
                nestedInputComponent.setAttribute(name, value);
            } else {
                nestedInputComponent.removeAttribute(name);
            }
        }

        if (value) {
            this.setAttribute(name, value);
        } else {
            this.removeAttribute(name);
        }
    }

    // Some form-inputs (such as c-form-input-presigned-fileupload) require
    // some pre-processing before the value can be collected for form
    // submission, in that case c-form can wait until preprocessing is done.
    preprocess() {
        const nestedInputComponent = this.getNestedInputComponent();
        if (nestedInputComponent && "preprocess" in nestedInputComponent) {
            return nestedInputComponent.preprocess();
        }
    }

    getNestedInputComponent(): IFormInputTypeComponent<ValueType> | undefined {
        // Sometimes this function is called before the connectedCallback is
        // triggered. this.target can point to the shadow dom, but the shadow dom
        // is only configured during the connectedCallback.
        if (!this.target) {
            return;
        }

        return this.target.querySelector<IFormInputTypeComponent<ValueType>>(
            `c-form-input-${this.targetComponentTagName}`
        );
    }
    // When the value attribute is changed, be sure to update the nested nodes
    // to reflect this change.
    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (name === "data-read-only") {
            const nestedInputComponent = this.getNestedInputComponent();
            if (nestedInputComponent) {
                nestedInputComponent.dataset.readOnly = newValue;
            }
        }

        if (name === "data-selected-label") {
            const nestedInputComponent = this.getNestedInputComponent();
            if (nestedInputComponent) {
                nestedInputComponent.dataset.selectedLabel = newValue;
            }
        }

        if (name === "data-prepend-value") {
            const nestedInputComponent = this.getNestedInputComponent();
            if (nestedInputComponent) {
                nestedInputComponent.dataset.prependValue = newValue;
            }
        }

        if (name === "data-append-value") {
            const nestedInputComponent = this.getNestedInputComponent();
            if (nestedInputComponent) {
                nestedInputComponent.dataset.appendValue = newValue;
            }
        }

        if (name === "data-value") {
            const nestedInputComponent = this.getNestedInputComponent();
            if (nestedInputComponent) {
                nestedInputComponent.dataset.value = newValue;
            }
        }

        if (name === "data-choices") {
            const nestedInputComponent = this.getNestedInputComponent();
            if (nestedInputComponent) {
                nestedInputComponent.dataset.choices = newValue;
            }
        }

        if (name === "data-meta-entries") {
            const nestedInputComponent = this.getNestedInputComponent();
            if (nestedInputComponent) {
                nestedInputComponent.dataset.metaEntries = newValue;
            }
        }

        if (name === "data-min") {
            const nestedInputComponent = this.getNestedInputComponent();
            if (nestedInputComponent) {
                nestedInputComponent.dataset.min = newValue;
            }
        }

        if (name === "data-max") {
            const nestedInputComponent = this.getNestedInputComponent();
            if (nestedInputComponent) {
                nestedInputComponent.dataset.max = newValue;
            }
        }
    }

    isConfigured() {
        if (!this.dataset.name) {
            throw Error(
                `'${this.tagName}' requires the 'data-name' attribute to be configured`
            );
        }

        if (!this.type) {
            throw Error(
                `'${this.tagName}' requires the 'data-type' attribute to be configured`
            );
        }

        if (!supportedTypes[this.type]) {
            throw new Error(
                `'${this.type}' not yet supported by ${this.tagName} (used by '${this.name}')`
            );
        }

        const props = supportedTypes[this.type];

        [...props.requiredProps, ...props.optionalProps].forEach((prop) => {
            const isRequiredProp =
                supportedTypes[this.type].requiredProps.includes(prop);
            const isOptionalProp =
                supportedTypes[this.type].optionalProps.includes(prop);

            // Converting to kebab case for error message only.
            let convertedPropToKebab;
            try {
                convertedPropToKebab = prop
                    .split(/[\s_\b]|(?=[A-Z])/)
                    .join("-")
                    .toLowerCase();
            } catch (_error) {
                // Assign prop to convertedPropToKebab as a fallback
                convertedPropToKebab = prop;
            }
            if (isOptionalProp && isRequiredProp) {
                throw new Error(
                    `'${this.tagName}' requires 'data-${convertedPropToKebab}' to only be in one category either required or optional for type '${this.type}'.`
                );
            }
            if (isRequiredProp && !(prop in this.dataset)) {
                throw new Error(
                    `'${this.tagName}' requires 'data-${convertedPropToKebab}' to be set for type '${this.type}'.`
                );
            }
        });

        for (const key of Object.keys(props.secretProps)) {
            if (key in this.dataset) {
                throw new Error(
                    `Cannot use secret-prop '${key}' as property for type '${this.type}'. The property '${key}' must be initialized in form-input supportedTypes.`
                );
            }
        }
    }

    async waitUntilInitialized() {
        await customElements.whenDefined(
            `c-form-input-${this.targetComponentTagName}`
        );
        await this.getNestedInputComponent().isInitialized;
    }

    async waitUntilPrefilled() {
        await this.getNestedInputComponent().isPrefilled;
    }

    // Sometimes not all fields are set for a given form-input. In that case,
    // we just try to fill all fields that are not set automatically using
    // the properties defined for this field
    autoConfigure() {
        // We can't do anything when we aren't given a name.
        if (!this.dataset.name) {
            return;
        }

        // Only try to auto-configure when type is not set. Otherwise just assume
        // all props for the given type are set.
        if (this.dataset.type !== undefined) {
            return;
        }

        const props = form.getPropsForFormInput(this);

        // If there are no properties for this form-input then there is no way to enrich it.
        if (!props) {
            return;
        }

        // Check which props ARE set, and build an object with the props that
        // should still be set.
        const fakeFormInput = { dataset: {} };
        form.enrichFormInput(fakeFormInput, this.dataset.name, props);

        // Any properties that have not been set for these field, are set
        // automatically.
        Object.entries(fakeFormInput.dataset).forEach(
            ([key, value]: [string, string]) => {
                if (!(key in this.dataset)) {
                    this.dataset[key] = value;
                }
            }
        );
    }

    renderInput() {
        const props = supportedTypes[this.type];

        const formInputElement = document.createElement(
            `c-form-input-${this.targetComponentTagName}`
        );

        [...props.requiredProps, ...props.optionalProps].forEach((prop) => {
            const value = this.dataset[prop];
            if (value !== undefined) {
                formInputElement.dataset[prop] = value;
            }
        });

        for (const [key, value] of Object.entries<string>(props.secretProps)) {
            if (value !== undefined) {
                formInputElement.dataset[key] = value;
            }
        }

        // Set the value for the input form, if required.
        if (this.value) {
            // Note that we are not setting the value directly through the setter
            // for 'value' on the component, but setting it on dataset instead.
            // Reason is that the setter might not be active yet, since there
            // is a brief period between when the component is being fetched and
            // when the component being initialized, in which the setter is not yet
            // present on the formInputElement. The dataset however, is.
            formInputElement.dataset.value = `${this.value}`;
        }

        // Always set the name to the nested form input.
        if (this.name) {
            formInputElement.dataset.name = this.name;
        }

        this.target.appendChild(formInputElement);
    }

    connectedCallback() {
        this.autoConfigure();

        // Instead of letting the component error out somewhere during its
        // lifetime, pre-check if all required conditions have been fulfilled.
        // That way we can guarantee that is will function correctly.
        this.isConfigured();
        this.renderInput();
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "c-form-input": FormInput;
    }
}
